import { useEffect, useMemo, useState } from "react";
import { Icon } from "../components/Icon.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { AiButton, useAction } from "./fields.js";
import { PageEditor } from "./PageEditor.js";
import { QueueBar } from "./QueueBar.js";
import { STATUS_LABEL, type ColoringPage } from "./types.js";

/** The page planner: a visual storyboard of every page in the book. */
export function PagesPanel() {
  const { projectId, state, reload } = useColoring();
  const toast = useToast();
  const { book, pages, characters, jobs } = state;
  const { busy, run } = useAction();
  const openJobs = new Map(jobs.filter((j) => j.status === "queued" || j.status === "generating").map((j) => [j.pageId, j.status]));
  const failedJobs = new Set(jobs.filter((j) => j.status === "error").map((j) => j.pageId));

  const [target, setTarget] = useState(book.pageCount);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmReplan, setConfirmReplan] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ColoringPage | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);

  useEffect(() => setTarget(book.pageCount), [book.pageCount]);
  useEffect(() => setLocalOrder(null), [pages]);

  const ordered = useMemo(() => {
    if (!localOrder) return pages;
    const byId = new Map(pages.map((p) => [p.id, p]));
    return localOrder.map((id) => byId.get(id)).filter((p): p is ColoringPage => !!p);
  }, [pages, localOrder]);

  const hasConcept = !!(book.concept ?? "").trim();
  const unstarted = pages.filter((p) => p.status === "planned" && !p.currentVariantId).length;

  const plan = (replaceUnstarted: boolean) =>
    run("plan", async () => {
      const { result } = await coloringApi.planBook(projectId, { pageCount: target, replaceUnstarted });
      await reload();
      if (result.created.length === 0) {
        toast.info(`The book already has ${result.kept} pages. Raise the page count to plan more.`);
      } else {
        toast.success(`Planned ${result.created.length} new page${result.created.length === 1 ? "" : "s"}.`);
      }
    });

  const addPage = () =>
    run("add", async () => {
      const { page } = await coloringApi.createPage(projectId, { title: `Page ${pages.length + 1}` });
      await reload();
      setEditingId(page.id);
    });

  const removePage = async (p: ColoringPage) => {
    setConfirmDelete(null);
    await run("delete", async () => {
      await coloringApi.deletePage(projectId, p.id);
      await reload();
    });
  };

  const drop = async (targetId: string) => {
    const from = dragId;
    setDragId(null);
    setOverId(null);
    if (!from || from === targetId) return;
    const ids = ordered.map((p) => p.id);
    const fromIdx = ids.indexOf(from);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, from);
    setLocalOrder(ids);
    try {
      await coloringApi.reorderPages(projectId, ids);
      await reload();
    } catch (err) {
      setLocalOrder(null);
      toast.error(err instanceof Error ? err.message : "Could not reorder.");
    }
  };

  const editing = pages.find((p) => p.id === editingId) ?? null;

  return (
    <div className="coloring-scroll">
      <div className="pages-toolbar">
        <div className="row gap-2" style={{ flexWrap: "wrap" }}>
          <div className="field" style={{ margin: 0, width: 110 }}>
            <label className="label">Pages in book</label>
            <input className="input" type="number" min={1} max={200} value={target} onChange={(e) => setTarget(Number(e.target.value))} />
          </div>
          <AiButton
            kind="primary"
            busy={busy === "plan"}
            onClick={() => void plan(false)}
            disabled={!hasConcept}
            title={!hasConcept ? "Write a concept on the Overview first" : "Adds new page concepts until the book has this many pages"}
          >
            Plan Book
          </AiButton>
          {unstarted > 0 && (
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirmReplan(true)} disabled={busy !== null || !hasConcept}>
              Re-plan unstarted pages
            </button>
          )}
          <button className="btn btn-sm" onClick={() => void addPage()} disabled={busy !== null}>
            <Icon name="plus" size={14} /> Add page
          </button>
        </div>
        <span className="faint" style={{ fontSize: 12 }}>
          {pages.length} of {book.pageCount} planned · {pages.filter((p) => p.currentVariantId).length} with artwork · {pages.filter((p) => p.status === "approved").length} approved
        </span>
      </div>

      <QueueBar />

      {pages.length === 0 ? (
        <div className="coloring-empty">
          <Icon name="image" size={30} style={{ color: "var(--text-faint)" }} />
          <h3>No pages yet</h3>
          <p className="muted">
            {hasConcept
              ? "Click Plan Book and the AI will propose varied page concepts you can edit, reorder and delete. No images are generated until you choose."
              : "Write your concept on the Overview tab, then plan the book here — or add pages by hand."}
          </p>
        </div>
      ) : (
        <div className="page-grid">
          {ordered.map((p, i) => (
            <div
              key={p.id}
              className={`page-card ${dragId === p.id ? "dragging" : ""} ${overId === p.id && dragId !== p.id ? "drop-target" : ""}`}
              draggable
              onDragStart={() => setDragId(p.id)}
              onDragEnd={() => {
                setDragId(null);
                setOverId(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (overId !== p.id) setOverId(p.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                void drop(p.id);
              }}
            >
              <button className="page-thumb" onClick={() => setEditingId(p.id)} aria-label={`Open page ${i + 1}`}>
                {p.currentVariantId ? (
                  <img src={coloringApi.imageUrl(projectId, p.currentVariantId)} alt={p.title} loading="lazy" />
                ) : (
                  <span className="page-thumb-empty">
                    <Icon name="image" size={22} />
                  </span>
                )}
                <span className="page-number">{i + 1}</span>
                {openJobs.has(p.id) && (
                  <span className="page-thumb-busy">
                    <Icon name="clock" size={16} className={openJobs.get(p.id) === "generating" ? "spin" : ""} />
                    {openJobs.get(p.id) === "generating" ? "Generating" : "Waiting"}
                  </span>
                )}
                {!openJobs.has(p.id) && failedJobs.has(p.id) && <span className="page-thumb-fail"><Icon name="alert" size={12} /> Failed</span>}
              </button>
              <div className="page-card-meta">
                <div className="page-card-title truncate" title={p.title}>{p.title || "Untitled page"}</div>
                <div className="row gap-2" style={{ justifyContent: "space-between" }}>
                  <span className={`status-badge status-${p.status}`}>{STATUS_LABEL[p.status]}</span>
                  <button className="btn btn-icon btn-ghost btn-sm" title="Delete page" onClick={() => setConfirmDelete(p)}>
                    <Icon name="trash" size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PageEditor
          page={editing}
          index={ordered.findIndex((p) => p.id === editing.id)}
          total={ordered.length}
          characters={characters}
          onClose={() => setEditingId(null)}
          onNavigate={(delta) => {
            const idx = ordered.findIndex((p) => p.id === editing.id) + delta;
            const next = ordered[idx];
            if (next) setEditingId(next.id);
          }}
        />
      )}

      {confirmReplan && (
        <ConfirmDialog
          title="Re-plan unstarted pages?"
          message={`${unstarted} page${unstarted === 1 ? " has" : "s have"} no artwork yet and will be replaced with a fresh plan — including any edits you made to them. Pages with artwork are kept.`}
          confirmLabel="Re-plan"
          danger
          onConfirm={() => {
            setConfirmReplan(false);
            void plan(true);
          }}
          onCancel={() => setConfirmReplan(false)}
        />
      )}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete this page?"
          message={`"${confirmDelete.title || "Untitled page"}" and its generated artwork records will be removed.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => void removePage(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
